import * as React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { SVGs } from "../assets";
import Button from "./Button";
import InputOptions from "./InputOptions";
import Text from "./Text";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export type PaginationProps = {
  page: number;
  pageSize: number;
  totalItems: number;
  loading?: boolean;
  onPageChange?: (page: number) => void;
  /** When set, a page size menu is shown and this is called with the new size. */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * When set, pagination controls are replaced with a single "Load more" button.
   * Useful for lazy-loading / incremental data fetching.
   */
  onLoadMore?: () => void | Promise<void>;
  /** Preset sizes for the menu; current `pageSize` is included if missing. */
  pageSizeOptions?: readonly number[];
  className?: string;
};

export function getPaginationPageItems(
  page: number,
  totalPages: number,
): (number | "ellipsis")[] {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (page <= 5) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  const lastStart = Math.max(2, totalPages - 4);
  if (page >= totalPages - 4) {
    const tail = Array.from(
      { length: totalPages - lastStart + 1 },
      (_, i) => lastStart + i,
    );
    return [1, "ellipsis", ...tail];
  }
  return [1, "ellipsis", page - 1, page, page + 1, "ellipsis", totalPages];
}

export default function Pagination({
  page,
  pageSize,
  totalItems,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onLoadMore,
  pageSizeOptions,
  className,
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(totalItems, page * pageSize);
  const pageItems = getPaginationPageItems(page, totalPages);
  const sizeMenuOptions = React.useMemo(() => {
    const base = pageSizeOptions?.length
      ? [...pageSizeOptions]
      : [...DEFAULT_PAGE_SIZE_OPTIONS];
    if (!base.includes(pageSize)) base.push(pageSize);
    return base
      .sort((a, b) => a - b)
      .map((n) => ({
        value: String(n),
        label: t("pagination.perPage", { size: n }),
      }));
  }, [pageSize, pageSizeOptions, t]);
  const canLoadMore = onLoadMore && end < totalItems;

  return (
    <div
      className={twMerge(
        "relative flex items-center justify-start lg:justify-center xlg:justify-center  gap-3",
        className,
      )}
    >
      {onLoadMore && (
        <Button
          onClick={onLoadMore}
          loading={loading}
          disabled={!canLoadMore}
          className="min-w-[120px]"
        >
          {t("pagination.loadMore")}
        </Button>
      )}
      <div className="absolute right-0 flex items-center gap-3">
        <Text className="text-text-secondary">
          {t("pagination.range", { start, end, totalItems })}
        </Text>
        {onLoadMore ? (
          <Button
            color="secondaryDark"
            onClick={onLoadMore}
            loading={loading}
            disabled={!canLoadMore}
            className="h-[24px] px-[8px] rounded-[2px] min-w-[86px]"
          >
            {t("pagination.loadMore")}
          </Button>
        ) : (
          <>
            <div className="flex items-center">
              <div
                onClick={() => onPageChange?.(Math.max(1, page - 1))}
                className="cursor-pointer w-[24px] h-[24px] flex items-center justify-center"
              >
                <img
                  src={SVGs.chevronLeft}
                  className="h-[12px]"
                  alt="chevron-left"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {pageItems.map((item) => {
                  if (item === "ellipsis") return <Text children="…" />;
                  const isCurrent = item === page;
                  return (
                    <button
                      key={item}
                      type="button"
                      aria-label={t("pagination.page", { page: item })}
                      onClick={() => onPageChange?.(item)}
                      children={<Text>{item}</Text>}
                      aria-current={isCurrent ? "page" : undefined}
                      className={twMerge(
                        "flex items-center justify-center rounded-[2px] border bg-surface h-[24px] w-[24px]  transition-colors",
                        isCurrent ? "border-text-primary" : "border-none",
                      )}
                    />
                  );
                })}
              </div>
              <div
                onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
                className="cursor-pointer w-[24px] h-[24px] flex items-center justify-center"
              >
                <img
                  src={SVGs.chevronLeft}
                  className="h-[12px] rotate-180"
                  alt="chevron-left"
                />
              </div>
            </div>
            {onPageSizeChange && (
              <InputOptions
                className="w-auto shrink-0"
                value={String(pageSize)}
                options={sizeMenuOptions}
                classNames={{
                  menu: "left-auto right-0 min-w-[120px]",
                }}
                onChange={(v) => onPageSizeChange(Number(v))}
              >
                <button
                  type="button"
                  aria-label={t("pagination.rowsPerPage")}
                  className={twMerge(
                    "flex h-[24px] shrink-0 items-center gap-[15px] rounded-[2px] border border-divider bg-surface px-[8px]",
                  )}
                >
                  <Text>{t("pagination.perPage", { size: pageSize })}</Text>
                  <img
                    src={SVGs.chevronLeft}
                    alt=""
                    className="h-[12px] -rotate-90"
                  />
                </button>
              </InputOptions>
            )}
          </>
        )}
      </div>
    </div>
  );
}
